import { Route } from "@/routes/(auth)/(headless)/reports/clients/$id.capital-gains";
import { imageAssetUrl } from "@/utils/url";
import { format } from "date-fns";
import { convertToCurrency } from "creso-core/utils";
import { useCallback, useMemo } from "react";
import {
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableCell,
  TableRow,
} from "web-design-system";
import { AmcLogo } from "@core/components/logo/amc-logo";
import { StickyNote } from "lucide-react";
import { useLoaderData } from "@tanstack/react-router";
import { JOINT_CLIENT_HOLDING_PATTERNS } from "creso-core/constants";
import { ReportHeader } from "../components/report-header";
import { ReportContainer } from "../components/report-container";
import { ReportFooter } from "../components/report-footer";
import { DistributorProfileSummary } from "../components/distributor-profile-summary";
import { EmployeePortfolioSummary } from "../components/employee-portfolio-summary";

type ReportColumnConfig<T> = Array<{
  name: string;
  render: (data: T, index: number) => React.ReactNode;
  className?: string;
}>;

// Fields of a capital-gains `scheme_level` row used by the summary tables
type SummaryRow = {
  scheme_name: string;
  amc_morning_star_code: string;
  sell_date: string;
  folio_number: string;
  purchase_price: string;
  sell_price: string;
  fund_type: string;
  short_term_capital_gain: number;
  long_term_capital_gain: number;
};

type SummaryTotals = ReturnType<typeof getTotalStats>;

function getTotalStats(rows: SummaryRow[]) {
  return rows.reduce(
    (acc, curr) => {
      acc.total_purchase_amount += Number(curr.purchase_price);
      acc.total_sale_amount += Number(curr.sell_price);
      acc.total_stcg += curr.short_term_capital_gain;
      acc.total_ltcg += curr.long_term_capital_gain;
      return acc;
    },
    {
      total_purchase_amount: 0,
      total_sale_amount: 0,
      total_stcg: 0,
      total_ltcg: 0,
    }
  );
}

function SummaryTable({
  data,
  columns,
  totals,
}: {
  data: SummaryRow[];
  columns: ReportColumnConfig<SummaryRow>;
  totals: SummaryTotals;
}) {
  if (!data.length) return null;

  return (
    <Table className="border border-border rounded-lg">
      <TableHeader className="bg-muted rounded-t-lg">
        <TableRow>
          {columns.map((column) => (
            <TableHead className={column.className} key={column.name}>
              {column.name}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map((row, idx) => (
          <TableRow key={idx} className="print:break-inside-avoid">
            {columns.map((column) => (
              <TableCell className={column.className} key={column.name}>
                {column.render(row, idx)}
              </TableCell>
            ))}
          </TableRow>
        ))}
        <TableRow className="bg-muted font-semibold print:break-inside-avoid">
          <TableCell colSpan={3} className="text-center">
            Total
          </TableCell>
          <TableCell className="text-right">
            {convertToCurrency(totals.total_sale_amount, 2)}
          </TableCell>
          <TableCell className="text-right">
            {convertToCurrency(totals.total_purchase_amount, 2)}
          </TableCell>
          <TableCell className="text-right">
            {convertToCurrency(totals.total_stcg, 2)}
          </TableCell>
          <TableCell className="text-right">
            {convertToCurrency(totals.total_ltcg, 2)}
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
}

export function CapitalGainsReport() {
  const {
    distributorBranding,
    distributorProfile,
    client,
    reportData,
    employee,
  } = useLoaderData({
    from: "/(auth)/(headless)/reports/clients/$id/capital-gains",
  });

  const { start_date, end_date } = Route.useSearch();

  const summaryData =
    reportData?.scheme_level?.toSorted((a, b) => {
      return a.sell_date.localeCompare(b.sell_date);
    }) || [];

  const equitySchemesSummaryData = summaryData.filter(
    (s) => s.fund_type === "Equity"
  );

  const hybridSchemesSummaryData = summaryData.filter(
    (s) => s.fund_type === "Hybrid"
  );

  // Everything that isn't Equity or Hybrid is reported under Debt
  const debtSchemesSummaryData = summaryData.filter(
    (s) => s.fund_type !== "Equity" && s.fund_type !== "Hybrid"
  );

  const equityTotalStats = getTotalStats(equitySchemesSummaryData);
  const debtTotalStats = getTotalStats(debtSchemesSummaryData);
  const hybridTotalStats = getTotalStats(hybridSchemesSummaryData);

  const columnDefGenerator: (
    schemeLabel: string
  ) => ReportColumnConfig<SummaryRow> = useCallback(
    (schemeLabel: string) => [
      {
        name: "",
        render: (_, index) => <span className="text-sm">{index + 1}</span>,
        className: "w-12",
      },
      {
        name: "Sell date",
        render: (data) => (
          <span className="text-sm">
            {format(data.sell_date, "dd MMM yyyy")}
          </span>
        ),
        className: "w-40",
      },
      {
        name: schemeLabel,
        render: (data) => (
          <div className="flex flex-row gap-2 items-center">
            <AmcLogo amc_code={data.amc_morning_star_code} />
            <div className="flex flex-col gap-1">
              <span className="text-sm">{data.scheme_name}</span>
              <span className="text-xs text-muted-foreground">
                Folio: {data.folio_number} &bull; {data.fund_type}
              </span>
            </div>
          </div>
        ),
        className: "w-70",
      },
      {
        name: "Sale value",
        render: (data) => (
          <span className="text-sm font-medium">
            {convertToCurrency(data.sell_price, 2)}
          </span>
        ),
        className: "text-right w-35",
      },
      {
        name: "Purchase value",
        render: (data) => (
          <span className="text-sm font-medium">
            {convertToCurrency(data.purchase_price, 2)}
          </span>
        ),
        className: "text-right w-35",
      },
      {
        name: "STCG",
        render: (data) => (
          <span className="text-sm font-medium">
            {convertToCurrency(data.short_term_capital_gain, 2)}
          </span>
        ),
        className: "text-right w-35",
      },
      {
        name: "LTCG",
        render: (data) => (
          <span className="text-sm font-medium">
            {convertToCurrency(data.long_term_capital_gain, 2)}
          </span>
        ),
        className: "text-right w-35",
      },
    ],
    []
  );

  const equitySummaryColumns = useMemo(
    () => columnDefGenerator("Equity scheme"),
    [columnDefGenerator]
  );

  const debtSummaryColumns = useMemo(
    () => columnDefGenerator("Debt scheme"),
    [columnDefGenerator]
  );

  const hybridSummaryColumns = useMemo(
    () => columnDefGenerator("Hybrid scheme"),
    [columnDefGenerator]
  );

  const transactionColumns: ReportColumnConfig<
    (typeof reportData.transaction_details)[number]
  > = useMemo(() => {
    return [
      {
        name: "",
        render: (_, index) => <span className="text-sm">{index + 1}</span>,
      },
      {
        name: "Purchase date",
        render: (data) => (
          <span className="text-sm">
            {data.purchase_date
              ? format(data.purchase_date, "dd MMM yyyy")
              : "-"}
          </span>
        ),
      },
      {
        name: "Purchase value",
        render: (data) => (
          <span className="text-sm">
            {convertToCurrency(data.purchase_value, 2)}
          </span>
        ),
        className: "text-right",
      },
      {
        name: "Units",
        render: (data) => (
          <span className="text-sm">{data.purchase_units}</span>
        ),
        className: "text-right",
      },
      {
        name: "Sale value",
        render: (data) => (
          <span className="text-sm font-medium">
            {convertToCurrency(data.redemption_value, 2)}
          </span>
        ),
        className: "text-right",
      },
      {
        name: "STCG",
        render: (data) => (
          <span className="text-sm font-medium">
            {convertToCurrency(data.stcg, 2)}
          </span>
        ),
        className: "text-right",
      },
      {
        name: "LTCG",
        render: (data) => (
          <span className="text-sm font-medium">
            {convertToCurrency(data.ltcg, 2)}
          </span>
        ),
        className: "text-right",
      },
    ];
  }, []);

  const noTransactions =
    !reportData?.transaction_details ||
    reportData?.transaction_details.length === 0;

  const groupTransactionByScheme = useMemo(() => {
    return reportData?.transaction_details
      ?.filter((t) => !t.is_total_row)
      ?.reduce(
        (acc, transaction) => {
          const { scheme_isin, folio_number, redemption_date } = transaction;
          if (!acc[`${scheme_isin}-${folio_number}-${redemption_date}`]) {
            acc[`${scheme_isin}-${folio_number}-${redemption_date}`] = [];
          }
          acc[`${scheme_isin}-${folio_number}-${redemption_date}`].push(
            transaction
          );
          return acc;
        },
        {} as Record<string, typeof reportData.transaction_details>
      );
  }, [reportData]);

  const displayOrder = useMemo(
    () =>
      Object.keys(groupTransactionByScheme).toSorted((a, b) => {
        const aDate = new Date(
          groupTransactionByScheme[a][0].redemption_date ?? ""
        );
        const bDate = new Date(
          groupTransactionByScheme[b][0].redemption_date ?? ""
        );
        return aDate.getTime() - bDate.getTime();
      }),
    [groupTransactionByScheme]
  );

  return (
    <>
      <ReportHeader
        distributorBranding={distributorBranding}
        profile={distributorProfile}
      />
      <ReportContainer>
        <div className="content px-8 w-full">
          <div className="flex h-[calc(100vh-150px)] flex-row">
            <div className="flex flex-col flex-1 h-full pt-15 pb-4 justify-between">
              <div className="flex flex-col gap-1">
                <span className="text-2xl font-semibold">{client?.name}</span>
                <span className="text-sm text-muted-foreground">
                  {client.pan} | {client.phone_number}
                  {client.holding_pattern &&
                  JOINT_CLIENT_HOLDING_PATTERNS.includes(client.holding_pattern)
                    ? " | Joint"
                    : null}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="font-semibold text-4xl">
                  Capital Gains Report
                </span>
                <span className="text-lg font-semibold">
                  ({format(start_date, "dd MMM yyyy")} -{" "}
                  {format(end_date, "dd MMM yyyy")})
                </span>
                <span className="text-sm text-muted-foreground">
                  Generated on {format(new Date(), "dd MMM yyyy")}
                </span>
              </div>
              {employee ? (
                <EmployeePortfolioSummary
                  employee={employee}
                  distributorProfile={distributorProfile}
                />
              ) : (
                <DistributorProfileSummary
                  distributorProfile={distributorProfile}
                />
              )}
            </div>
            <div className="flex-1 h-full">
              <img
                src={imageAssetUrl("capital-gains-report.webp")}
                alt="client-portfolio"
                className="h-full w-full bg-transparent object-contain"
              />
            </div>
          </div>
          <div className="flex flex-col gap-5 min-h-[calc(100vh-200px)]">
            <h3 className="break-before-page mt-15 print:mt-0">
              {`Capital Gains Report - ${format(start_date, "dd MMM yyyy")} to ${format(
                end_date,
                "dd MMM yyyy"
              )}`}
            </h3>
            <div className="flex flex-row gap-4">
              <div className="flex flex-4 flex-row bg-muted border border-border rounded-lg p-5">
                <div className="flex flex-1 flex-col gap-2 items-start">
                  <span className="text-sm">Equity STCG</span>
                  <span className="text-base font-semibold">
                    {convertToCurrency(reportData?.equity_stcg, 2)}
                  </span>
                </div>
                <div className="flex flex-1 flex-col gap-2 items-start">
                  <span className="text-sm">Equity LTCG</span>
                  <span className="text-base font-semibold">
                    {convertToCurrency(reportData?.equity_ltcg, 2)}
                  </span>
                </div>
              </div>
              <div className="flex flex-4 flex-row bg-muted border border-border rounded-lg p-5">
                <div className="flex flex-1 flex-col gap-2 items-start">
                  <span className="text-sm">Debt STCG</span>
                  <span className="text-base font-semibold">
                    {convertToCurrency(reportData?.debt_stcg, 2)}
                  </span>
                </div>
                <div className="flex flex-1 flex-col gap-2 items-start">
                  <span className="text-sm">Debt LTCG</span>
                  <span className="text-base font-semibold">
                    {convertToCurrency(reportData?.debt_ltcg, 2)}
                  </span>
                </div>
              </div>
              <div className="flex flex-4 flex-row bg-muted border border-border rounded-lg p-5">
                <div className="flex flex-1 flex-col gap-2 items-start">
                  <span className="text-sm">Hybrid STCG</span>
                  <span className="text-base font-semibold">
                    {convertToCurrency(reportData?.hybrid_stcg, 2)}
                  </span>
                </div>
                <div className="flex flex-1 flex-col gap-2 items-start">
                  <span className="text-sm">Hybrid LTCG</span>
                  <span className="text-base font-semibold">
                    {convertToCurrency(reportData?.hybrid_ltcg, 2)}
                  </span>
                </div>
              </div>
              <div className="flex flex-2 flex-row bg-muted border border-border rounded-lg p-5">
                <div className="flex flex-1 flex-col gap-2 items-start">
                  <span className="text-sm">Total capital gain</span>
                  <span className="text-base font-semibold">
                    {convertToCurrency(reportData?.total_capital_gain, 2)}
                  </span>
                </div>
              </div>
            </div>
            <div className="h-px bg-border w-full" />
            <h3>Transaction Summary</h3>
            <SummaryTable
              data={equitySchemesSummaryData}
              columns={equitySummaryColumns}
              totals={equityTotalStats}
            />
            <SummaryTable
              data={debtSchemesSummaryData}
              columns={debtSummaryColumns}
              totals={debtTotalStats}
            />
            <SummaryTable
              data={hybridSchemesSummaryData}
              columns={hybridSummaryColumns}
              totals={hybridTotalStats}
            />
            {noTransactions ? (
              <div className="flex-1 flex flex-row justify-between items-center bg-muted border border-boder/50 rounded-lg">
                <div className="flex flex-col text-muted-foreground w-full items-center">
                  <StickyNote
                    className="size-12"
                    strokeWidth={1}
                    strokeDasharray={2}
                  />
                  <span className="mt-6">No transactions found</span>
                  <span>{`(${format(start_date, "dd MMM yyyy")} - ${format(
                    end_date,
                    "dd MMM yyyy"
                  )})`}</span>
                </div>
              </div>
            ) : null}
          </div>
          {!noTransactions ? (
            <div className="flex flex-col gap-5 min-h-[calc(100vh-150px)]">
              <h3 className="break-before-page mt-15 print:mt-0">
                Transactions
              </h3>
              {displayOrder.map((key) => {
                const transaction = groupTransactionByScheme[key][0];
                const totalSTCG = groupTransactionByScheme[key].reduce(
                  (acc, transaction) => acc + transaction.stcg,
                  0
                );
                const totalLTCG = groupTransactionByScheme[key].reduce(
                  (acc, transaction) => acc + transaction.ltcg,
                  0
                );
                const total_purchase_amount = groupTransactionByScheme[
                  key
                ].reduce(
                  (acc, transaction) => acc + transaction.purchase_value,
                  0
                );
                const total_sale_amount = groupTransactionByScheme[key].reduce(
                  (acc, transaction) => acc + transaction.redemption_value,
                  0
                );
                const total_units = groupTransactionByScheme[key].reduce(
                  (acc, transaction) => acc + transaction.purchase_units,
                  0
                );
                const categoryName = `${transaction.fund_type} ${transaction.fund_level_category_name}`;
                return (
                  <Table key={key} className="border border-border rounded-lg">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="px-px py-0">
                          <div className="flex flex-row gap-3 bg-muted p-4">
                            <AmcLogo
                              size="lg"
                              amc_code={transaction.amc_morning_star_code}
                            />
                            <div className="flex flex-col gap-1">
                              <span className="text-base font-semibold">
                                {transaction.scheme_name}
                              </span>
                              <div className="text-xs font-normal text-muted-foreground flex flex-row items-center">
                                {`${categoryName} `} &bull; Folio:{" "}
                                {transaction.folio_number}
                              </div>
                            </div>
                            <div className="flex flex-col ml-auto gap-1 items-end">
                              <span className="text-base font-semibold">
                                {convertToCurrency(total_sale_amount, 2)}
                              </span>
                              <span className="text-xs font-normal text-muted-foreground">
                                Sold on{" "}
                                {transaction.redemption_date
                                  ? format(
                                      transaction.redemption_date,
                                      "dd MMM yyyy"
                                    )
                                  : "-"}
                              </span>
                            </div>
                          </div>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell className="px-px py-0">
                          <Table className="rounded-lg">
                            <TableHeader className="bg-muted rounded-t-lg">
                              <TableRow>
                                {transactionColumns.map((column) => (
                                  <TableHead
                                    className={column.className}
                                    key={column.name}
                                  >
                                    {column.name}
                                  </TableHead>
                                ))}
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {groupTransactionByScheme[key]?.map(
                                (transaction, index) => (
                                  <TableRow
                                    key={index}
                                    className="print:break-inside-avoid"
                                  >
                                    {transactionColumns.map((column) => (
                                      <TableCell
                                        className={column.className}
                                        key={column.name}
                                      >
                                        {column.render(transaction, index)}
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                )
                              )}
                              <TableRow className="font-semibold bg-muted print:break-inside-avoid">
                                <TableHead className="text-center" colSpan={2}>
                                  Total
                                </TableHead>
                                <TableCell className="text-right">
                                  {convertToCurrency(total_purchase_amount, 2)}
                                </TableCell>
                                <TableCell className="text-right">
                                  {total_units.toFixed(2)}
                                </TableCell>
                                <TableCell className="text-right">
                                  {convertToCurrency(total_sale_amount, 2)}
                                </TableCell>
                                <TableCell className="text-right">
                                  {convertToCurrency(totalSTCG, 2)}
                                </TableCell>
                                <TableCell className="text-right">
                                  {convertToCurrency(totalLTCG, 2)}
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                );
              })}
            </div>
          ) : null}
          <div className="flex flex-col">
            <h4 className="break-before-page mt-15 mb-5 print:mt-0">
              Disclaimer:
            </h4>
            <div className="flex flex-col gap-6 text-sm font-normal">
              <p>
                This capital‑gains statement is provided for reference only and
                must not be used as the sole basis for tax filings.
              </p>
              <div className="flex flex-col gap-2">
                <p className="font-semibold">Note on methodology:</p>
                <ol className="list-decimal pl-5 flex flex-col gap-2">
                  <li>
                    Calculations follow FIFO. For equity units sold on or after
                    1 Apr 2018, the acquisition cost is the grandfathered NAV of
                    31 Jan 2018. For purchases made on or after 1 Jul 2020,
                    acquisition cost includes stamp duty.
                  </li>
                  <li>
                    Equity Funds: Long term after 12 months. For sales on or
                    after 23 Jul 2024, long term is taxed at 12.5% beyond the
                    ₹1.25 lakh annual exemption and short term at 20%.
                  </li>
                  <li>
                    Debt Funds: Short term at slab rate. Units bought on or
                    after 1 Apr 2023 are always short term; older units are long
                    term after 24 months, taxed at 12.5% for sales on or after
                    23 Jul 2024.
                  </li>
                  <li>
                    Hybrid Funds: Long term after 24 months; for sales on or
                    after 23 Jul 2024 it is taxed at 12.5% with no ₹1.25 lakh
                    exemption. Short term at slab rate. Kept apart from Equity
                    because neither treatment matches it.
                  </li>
                </ol>
              </div>
              <p>
                Values are rounded for brevity and exclude any STT or TDS
                actually deducted, so results may vary slightly if recomputed.
                Please verify all figures with a qualified tax advisor. The
                Company and its directors, employees, or agents are not liable
                for your tax computation or for any actions taken based on this
                statement.
              </p>
            </div>
          </div>
        </div>
      </ReportContainer>
      <ReportFooter>{client.name}&apos;s capital gains report</ReportFooter>
    </>
  );
}
